import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTelegram } from '@/hooks/useTelegram';
import { useAuth } from '@/hooks/useAuth';
import { api } from '@/services/api';

interface CharactersResponse {
  characters: { id: string }[];
  count: number;
}

export function ModeSelectPage() {
  const navigate = useNavigate();
  const { hapticFeedback } = useTelegram();
  const { character } = useAuth();
  const [isStarting, setIsStarting] = useState(false);
  const [showNewGameModal, setShowNewGameModal] = useState(false);
  const [hasCharacters, setHasCharacters] = useState<boolean | null>(null);

  // Check if user has any characters
  useEffect(() => {
    const checkCharacters = async () => {
      try {
        const response = await api.get<CharactersResponse>('/characters');
        setHasCharacters(response.count > 0);
      } catch (err) {
        console.error('Failed to check characters:', err);
        setHasCharacters(false);
      }
    };
    checkCharacters();
  }, []);

  const handleGMMode = () => {
    hapticFeedback('medium');
    localStorage.setItem('gameMode', 'gm');
    navigate('/gm-tools');
  };

  const handleContinueGame = () => {
    hapticFeedback('medium');
    localStorage.setItem('gameMode', 'solo');

    // Check if character exists
    if (character?.characterClass) {
      navigate('/adventure');
    } else {
      navigate('/character-select');
    }
  };

  const handleCharacterSelect = () => {
    hapticFeedback('medium');
    localStorage.setItem('gameMode', 'solo');
    navigate('/character-select');
  };

  const handleNewGame = () => {
    hapticFeedback('medium');
    localStorage.setItem('gameMode', 'solo');

    // If no character exists, go directly to character creation
    if (!character?.characterClass) {
      navigate('/character-creation');
      return;
    }

    // Show modal to choose: new character or current
    setShowNewGameModal(true);
  };

  const handleNewCharacter = () => {
    hapticFeedback('medium');
    setShowNewGameModal(false);
    navigate('/character-creation');
  };

  const handlePlayCurrentCharacter = async () => {
    hapticFeedback('medium');
    setShowNewGameModal(false);
    setIsStarting(true);

    try {
      await api.post('/game/session/new', {});
      navigate('/adventure/play');
    } catch (err) {
      console.error('Failed to start new game:', err);
      hapticFeedback('error');
      setIsStarting(false);
    }
  };

  const handleExit = () => {
    hapticFeedback('light');
    // Try to close Telegram Mini App
    if (window.Telegram?.WebApp?.close) {
      window.Telegram.WebApp.close();
    } else {
      // Fallback - go back in history or show message
      window.history.back();
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col safe-area-top safe-area-bottom">
      {/* Header with exit button */}
      <div className="p-4">
        <button
          onClick={handleExit}
          className="text-gray-400 hover:text-white transition-colors flex items-center gap-1"
        >
          ← Выйти
        </button>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        {/* Logo/Title */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-amber-500 mb-2">Dungeon World</h1>
          <p className="text-gray-400">Выберите режим игры</p>
        </motion.div>

        {/* Mode Cards */}
        <div className="w-full max-w-md space-y-4">
          {/* GM Mode */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleGMMode}
            className="w-full p-6 rounded-2xl bg-gradient-to-br from-gray-800 to-gray-850 border border-amber-500/20 hover:border-amber-500/40 transition-all text-left"
          >
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                <span className="text-4xl">🎭</span>
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-white mb-1">Вести игру мастером</h2>
                <p className="text-gray-400 text-sm">
                  Инструменты и справочник для ведения игры
                </p>
              </div>
            </div>
            <div className="mt-4 flex items-center text-amber-500 text-sm">
              <span>Открыть инструменты</span>
              <span className="ml-2">→</span>
            </div>
          </motion.button>

          {/* Solo Mode Card */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="w-full p-6 rounded-2xl bg-gradient-to-br from-gray-800 to-gray-850 border border-amber-500/20"
          >
            <div className="flex items-start gap-4 mb-4">
              <div className="w-16 h-16 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                <span className="text-4xl">⚔️</span>
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-white mb-1">Играть одному</h2>
                <p className="text-gray-400 text-sm">
                  AI-мастер проведёт для вас приключение
                </p>
              </div>
            </div>

            {/* Buttons for Solo mode */}
            <div className="space-y-2">
              {/* Show "Continue" only if user has characters */}
              {hasCharacters && (
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  onClick={handleContinueGame}
                  className="w-full py-3 px-4 rounded-xl bg-amber-500 text-black font-medium flex items-center justify-center gap-2 hover:bg-amber-400 transition-colors"
                >
                  <span>▶️</span>
                  <span>Продолжить игру</span>
                </motion.button>
              )}

              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={handleNewGame}
                disabled={isStarting}
                className={`w-full py-3 px-4 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 ${
                  hasCharacters
                    ? 'bg-gray-700 text-white hover:bg-gray-600'
                    : 'bg-amber-500 text-black hover:bg-amber-400'
                }`}
              >
                <span>✨</span>
                <span>{isStarting ? 'Создание...' : hasCharacters ? 'Новая игра' : 'Создать персонажа'}</span>
              </motion.button>

              {hasCharacters && (
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  onClick={handleCharacterSelect}
                  className="w-full py-2.5 px-4 rounded-xl bg-gray-800 border border-gray-700 text-gray-300 font-medium flex items-center justify-center gap-2 hover:bg-gray-700 transition-colors"
                >
                  <span>👥</span>
                  <span>Мои персонажи</span>
                </motion.button>
              )}
            </div>
          </motion.div>
        </div>

        {/* Footer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-12 text-gray-600 text-sm text-center"
        >
          Powered by Apocalypse World Engine
        </motion.p>
      </div>

      {/* New Game Modal */}
      <AnimatePresence>
        {showNewGameModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
            onClick={() => setShowNewGameModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="bg-gray-800 rounded-2xl p-6 max-w-sm w-full"
            >
              <h3 className="text-lg font-bold text-white mb-2 text-center">Новая игра</h3>
              <p className="text-gray-400 text-sm mb-6 text-center">
                Выберите персонажа для нового приключения
              </p>

              <div className="space-y-3">
                <button
                  onClick={handleNewCharacter}
                  className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-purple-600 to-purple-500 text-white font-medium flex items-center justify-center gap-2 hover:from-purple-500 hover:to-purple-400 transition-all"
                >
                  <span>✨</span>
                  <span>Создать нового персонажа</span>
                </button>

                <button
                  onClick={handlePlayCurrentCharacter}
                  disabled={isStarting}
                  className="w-full py-3 px-4 rounded-xl bg-amber-500 text-black font-medium flex items-center justify-center gap-2 hover:bg-amber-400 transition-colors disabled:opacity-50"
                >
                  <span>⚔️</span>
                  <span>
                    {isStarting ? 'Создание...' : `Играть за ${character?.name || 'текущего'}`}
                  </span>
                </button>

                <button
                  onClick={() => setShowNewGameModal(false)}
                  className="w-full py-2.5 rounded-xl bg-gray-700 text-gray-300 font-medium hover:bg-gray-600 transition-colors"
                >
                  Отмена
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
